import { RefCallback, useCallback, useContext, useEffect, useRef, useState } from 'react';

import AnimatedSpinner from '@rover/components/src/typography/AnimatedSpinner';
import { Box, Flex, Heading } from '@rover/kibble/core';
import MQ from '@rover/kibble/styles/MediaQuery';
import Review from '@rover/react-lib/src/components/modals/Review';
import ReviewReply from '@rover/react-lib/src/components/modals/Review/ReviewReply';
import { isReplyable } from '@rover/react-lib/src/components/modals/Review/utils';
import useMatchMedia from '@rover/react-lib/src/hooks/useMatchMedia';
import { useApiPersonDetailReviewsList } from '@rover/rsdk/src/apiClient/latest/api-person-detail-reviews-list';
import { ApiPersonDetailReviewsListServiceTypeCategory as ServiceTypeCategories } from '@rover/rsdk/src/apiClient/latest/model/apiPersonDetailReviewsListServiceTypeCategory';
import type { ReviewDispatch } from '@rover/rsdk/src/apiClient/latest/model/reviewDispatch';
import type { TrainerInsights } from '@rover/rsdk/src/apiClient/latest/model/trainerInsights';
import { Logger } from '@rover/rsdk/src/modules/Logging';

import InsightsOverview from './InsightsOverview';
import NoTrainingReviewsBox from './NoTrainingReviewsBox';
import ReviewsModalContext from './ReviewsModalContext';
import ServiceTypeReviewFilter from './ServiceTypeReviewFilter';
import StickyHeader from './StickyHeader';

export type TrainerReviewsInfo = {
  shortName: string;
  defaultImage: { small: string };
  trainerInsights: TrainerInsights;
};

export type FullMixedReviewsListProps = {
  personOpk: string;
  scrollIndex: number;
  trainerInfo: TrainerReviewsInfo;
  onLearnMore: () => void;
  onClickClose: () => void;
  shouldShowFilter: boolean;
};
const FullMixedReviewsList = ({
  personOpk,
  scrollIndex,
  trainerInfo,
  onLearnMore,
  onClickClose,
  shouldShowFilter,
}: FullMixedReviewsListProps): JSX.Element => {
  const reviewListRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | undefined>(undefined);
  const [page, setPage] = useState<number>(1);
  const [reviewsList, setReviewsList] = useState<ReviewDispatch[]>([]);
  const [isScrolling, setIsScrolling] = useState(false);
  /** A dummy element / workaround for observing the container scrolling */
  const scrollDummyRef = useRef<HTMLElement>(null);
  const stickyHeaderRef = useRef<HTMLElement>(null);
  const [scrollMarginTop, setScrollMarginTop] = useState<string>();
  const [lastHeight, setLastHeight] = useState<number | null>(null);
  const { serviceTypeCategory, setServiceTypeCategory } = useContext(ReviewsModalContext);
  const { data: reviewsData, isLoading: isLoadingReviews } = useApiPersonDetailReviewsList(
    personOpk,
    {
      page,
      serviceTypeCategory,
    }
  );
  const isMobile = useMatchMedia(MQ.XS_DOWN.getBreakpointString());

  // Scroll to the review that was clicked, if applicable
  useEffect(() => {
    if (scrollIndex <= 0) return;
    setTimeout(() => {
      if (process.env.JS_ENV_CLIENT) {
        const reviewToFocus = document.getElementById(`review-${scrollIndex}`);
        if (reviewToFocus === null) return;
        // Using a timeout here as autoscroll doesn't work right in staging/prod (due to animated modal height?)
        setTimeout(() => {
          reviewToFocus.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }, 150);
        (reviewToFocus.firstChild as HTMLElement).focus();
      }
    }, 100);
  }, [scrollIndex]);

  // Append new reviews to the list in state, or clear the list if loading the first page (with no cached results)
  useEffect(() => {
    if (page === 1) {
      setReviewsList(reviewsData?.results || []);
    } else if (reviewsData && reviewsData.results) {
      const newReviews = reviewsData.results;
      setReviewsList((prev) => [...prev, ...newReviews]);
    }
  }, [reviewsData, page]);

  // Track the review list height in state
  useEffect(() => {
    // If the observer is undefined, exit early and don't init them
    if (typeof ResizeObserver === 'undefined') {
      return () => {};
    }

    const resizeObserver = new ResizeObserver(() => {
      if (reviewListRef.current) {
        setLastHeight(reviewListRef.current.clientHeight);
      }
    });

    if (process.env.JS_ENV_CLIENT && reviewListRef.current) {
      resizeObserver.observe(reviewListRef.current);
    }
    return (): void => {
      if (process.env.JS_ENV_CLIENT) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  const getSpinnerHeight = (): string => {
    // If loading a new query of reviews, use the last height of the list to keep the modal from collapsing
    if (lastHeight && reviewsList.length === 0) {
      // eslint-disable-next-line rover/no-platform-specific-globals-or-imports -- Using platform guard
      if (process.env.JS_ENV_CLIENT && lastHeight > window.innerHeight) {
        return '100vh';
      }
      return `${lastHeight}px`;
    }
    return 'auto';
  };

  // Load more reviews when the last review is in view
  const lastReviewRefCallback = useCallback<RefCallback<HTMLElement>>(
    (node) => {
      if (isLoadingReviews) return;
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          setPage((p) => p + 1);
          Logger.info('trainer_profile_load_more_reviews');
        }
      });
      if (node) observerRef.current.observe(node);
    },
    [isLoadingReviews]
  );

  // Indirectly monitor the scrolling state of the container
  useEffect(() => {
    if (!scrollDummyRef.current) return undefined;

    const observer = new IntersectionObserver(([entry]) => {
      const isIntersecting = entry.intersectionRatio < 1;
      setIsScrolling(isIntersecting);
    });
    observer.observe(scrollDummyRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  // Determine a scrolling offset for reviews to account for the sticky header
  useEffect(() => {
    if (!stickyHeaderRef.current) return;
    const heightPx = stickyHeaderRef.current.offsetHeight;
    const topOffsetPx = isMobile ? stickyHeaderRef.current.getBoundingClientRect().top : 0;
    const scrollMarginTopPx = heightPx + topOffsetPx + 8;
    setScrollMarginTop(`${scrollMarginTopPx}px`);
  }, [isMobile]);

  const onChangeFilter = (category: ServiceTypeCategories): void => {
    setServiceTypeCategory(category);
    setPage(1);
  };

  const shouldShowSpinner = !isLoadingReviews ? false : !(page === 1 && reviewsList.length !== 0);

  const allReviews = reviewsList.map((review, index) => {
    const hasReply = isReplyable(review) && review?.response;
    return (
      <Box
        width="100%"
        key={`${review.url}-${index}`}
        id={`review-${index}`}
        sx={{
          paddingBottom: !hasReply ? '40px' : '0px',
          scrollMarginTop,
        }}
      >
        <Review
          review={review}
          {...(index === reviewsList.length - 1 &&
            reviewsData?.next && { ref: lastReviewRefCallback })}
          onLearnMore={onLearnMore}
          full
          variant="training"
        />
        {hasReply && review.response && (
          <Box pt="6x" sx={{ paddingBottom: '40px' }}>
            <ReviewReply reply={review.response} avatarUrl={trainerInfo.defaultImage.small} />
          </Box>
        )}
      </Box>
    );
  });

  return (
    <>
      <Box height="0px" ref={scrollDummyRef} />
      <StickyHeader
        trainerInfo={trainerInfo}
        onClickClose={onClickClose}
        isStuck={isScrolling}
        ref={stickyHeaderRef}
      />
      {trainerInfo.trainerInsights.allServiceAverageRating && (
        <InsightsOverview trainerInfo={trainerInfo} />
      )}
      <Flex
        width="100%"
        flexDirection={['column', 'row']}
        justifyContent={['initial', 'space-between']}
        gap="3x"
        pb="6x"
        pt="4x"
      >
        <Heading size="400" fontWeight="semibold" mb="0x">
          {/* TODO DEV-104195: Translate "Reviews" */}
          <Box sx={{ fontSize: '26px' }}>Reviews</Box>
        </Heading>
        {shouldShowFilter && (
          <ServiceTypeReviewFilter
            onChange={onChangeFilter}
            initialServiceTypeCategory={serviceTypeCategory}
          />
        )}
      </Flex>
      <Box ref={reviewListRef}>
        {allReviews}
        {!shouldShowSpinner &&
          allReviews.length === 0 &&
          serviceTypeCategory === 'dog-training' && <NoTrainingReviewsBox />}
        {shouldShowSpinner && (
          <Flex
            width="100%"
            justifyContent="center"
            alignItems="center"
            sx={{
              height: getSpinnerHeight(),
            }}
          >
            <AnimatedSpinner />
          </Flex>
        )}
      </Box>
    </>
  );
};

export default FullMixedReviewsList;
